import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";
import TimezoneSelect from "react-timezone-select";
import SelectedSlots from "./SelectedSlots";

export default function EditableCalendar({
  availability,
  setAvailability,
  timezone,
  setTimezone,
  setAvailabilityForm,
  show,
  setShow,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [scrollAreaHeight, setScrollAreaHeight] = useState(600);
  const [selectedSlotsHeight, setSelectedSlotsHeight] = useState(0);
  const weekDates = getWeekDates(currentDate);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const scrollAreaRef = useRef(null);
  const cardContentRef = useRef(null);
  const selectedSlotsRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (cardContentRef.current) {
        const cardContentHeight = cardContentRef.current.clientHeight;
        const maxScrollAreaHeight = cardContentHeight * 0.5;
        const newScrollAreaHeight = Math.min(600, maxScrollAreaHeight);
        setScrollAreaHeight(newScrollAreaHeight);
        setSelectedSlotsHeight(cardContentHeight - newScrollAreaHeight - 16);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isSelecting) {
        handleMouseUp();
      }
    };
    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isSelecting, selectionStart, selectionEnd]);

  const handleMouseDown = (date, hour) => {
    setIsSelecting(true);
    setSelectionStart({ date, hour });
    setSelectionEnd({ date, hour });
  };

  const handleMouseEnter = (date, hour) => {
    if (isSelecting) {
      setSelectionEnd({ date, hour });
    }
  };

  const handleMouseUp = () => {
    if (isSelecting && selectionStart && selectionEnd) {
      toggleAvailabilityRange(selectionStart, selectionEnd);
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
    }
  };

  const toggleAvailabilityRange = (start, end) => {
    const startDate = new Date(start.date);
    startDate.setUTCHours(start.hour, 0, 0, 0);
    const endDate = new Date(end.date);
    endDate.setUTCHours(end.hour, 0, 0, 0);

    const newAvailability = [...availability];
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const timestamp = Math.floor(currentDate.getTime() / 1000);
      const index = newAvailability.indexOf(timestamp);

      if (index === -1) {
        newAvailability.push(timestamp);
      } else {
        newAvailability.splice(index, 1);
      }

      currentDate.setUTCHours(currentDate.getUTCHours() + 1);
    }

    setAvailability(newAvailability);
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const isInSelectionRange = (date, hour) => {
    if (!isSelecting || !selectionStart || !selectionEnd) return false;
    const slotDate = new Date(date);
    slotDate.setUTCHours(hour, 0, 0, 0);
    const start = new Date(selectionStart.date);
    start.setUTCHours(selectionStart.hour, 0, 0, 0);
    const end = new Date(selectionEnd.date);
    end.setUTCHours(selectionEnd.hour, 0, 0, 0);
    return slotDate >= start && slotDate <= end;
  };

  const handleTimezoneChange = (newTimezone) => {
    setTimezone(newTimezone.value);
  };

  const isSlotSelected = (date, hour) => {
    const slotDate = new Date(date);
    slotDate.setUTCHours(hour, 0, 0, 0);
    const timestamp = Math.floor(slotDate.getTime() / 1000);
    return availability.includes(timestamp);
  };

  const formatTimeForTimezone = (date, hour) => {
    const slotDate = new Date(date);
    slotDate.setUTCHours(hour, 0, 0, 0);
    return slotDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: timezone,
    });
  };

  return (
    <Card className="w-full max-w-6xl mx-auto bg-white">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 border-b">
        <CardTitle className="text-xl font-semibold text-gray-700">
          Edit Availability
        </CardTitle>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevWeek}
            className="text-gray-600"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {!window.location.href.includes("consultant") && (
            <Button
              variant="outline"
              size="sm"
              className="text-gray-600"
              onClick={() => setShow(!show)}
            >
              Back
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextWeek}
            className="text-gray-600"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          <TimezoneSelect value={timezone} onChange={handleTimezoneChange} />
          {availability.length > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAvailabilityForm(false)}
                className="text-gray-600"
              >
                Confirm
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAvailability([])}
                className="text-gray-600"
              >
                Clear
              </Button>
            </>
          )}
        </div>
        <i
          className="fa-times fa-solid"
          onClick={() => {
            setAvailabilityForm(false);
          }}
        ></i>
      </CardHeader>
      <CardContent className="p-0" ref={cardContentRef}>
        <div className="grid grid-cols-8 border-b">
          <div className="p-2 border-r"></div>
          {weekDates.map((date, index) => (
            <div
              key={index}
              className="p-2 text-center border-r last:border-r-0"
            >
              <div className="text-sm font-medium text-gray-500">
                {date.toLocaleDateString("en-US", {
                  weekday: "short",
                  timeZone: timezone,
                })}
              </div>
              <div className="text-lg font-semibold text-gray-700">
                {date.toLocaleDateString("en-US", {
                  day: "numeric",
                  timeZone: timezone,
                })}
              </div>
            </div>
          ))}
        </div>
        <ScrollArea
          ref={scrollAreaRef}
          style={{ height: `${scrollAreaHeight}px` }}
          className="transition-all duration-300 ease-in-out"
        >
          <div className="grid grid-cols-8">
            {hours.map((hour) => (
              <React.Fragment key={hour}>
                <div className="p-2 text-right pr-4 border-r text-sm text-gray-500">
                  {formatTimeForTimezone(currentDate, hour)}
                </div>
                {weekDates.map((date, dateIndex) => {
                  const isWeekendDay = isWeekend(date);
                  const isSelected = isSlotSelected(date, hour);
                  return (
                    <div
                      key={`${hour}-${dateIndex}`}
                      className={`border-r border-b last:border-r-0 ${
                        isWeekendDay
                          ? "bg-gray-100"
                          : "hover:bg-gray-100 cursor-pointer"
                      }`}
                      onMouseDown={() =>
                        !isWeekendDay && handleMouseDown(date, hour)
                      }
                      onMouseEnter={() =>
                        !isWeekendDay && handleMouseEnter(date, hour)
                      }
                      onMouseUp={handleMouseUp}
                    >
                      {!isWeekendDay &&
                        (isSelected || isInSelectionRange(date, hour)) && (
                          <div className="w-full h-full bg-blue-200"></div>
                        )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </ScrollArea>
        <ScrollArea
          ref={selectedSlotsRef}
          style={{ height: `${selectedSlotsHeight}px` }}
          className="transition-all duration-300 ease-in-out"
        >
          <SelectedSlots
            availability={availability}
            timezone={timezone}
            maxHeight={selectedSlotsHeight}
          />
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

const getWeekDates = (date) => {
  const week = [];
  const startOfWeek = new Date(date);
  startOfWeek.setUTCHours(0, 0, 0, 0);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getUTCDay());

  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    week.push(day);
  }
  return week;
};

const isWeekend = (date) => {
  const day = date.getUTCDay();
  return day === 0 || day === 6;
};
